import React, { useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  TableCell,
  TableRow,
  Typography
} from '@esgian/esgianui';
import Tooltip from '@mui/material/Tooltip';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { useSelector } from 'react-redux';
import { getSessionStatus } from '@store/features';
import { useQuery } from '@tanstack/react-query';
import { useTimezone } from '@hooks/useTimezone';
import { getReportHistory, downloadExecutionReport } from '@api/ReportScheduler';
import moment from 'moment-timezone';
import { DATE_TIME_FORMAT } from '@constants/index';

function HistoryDetailsView({ reportId, numColumns }) {
  const sessionStatus = useSelector(getSessionStatus);
  const [downloadingIds, setDownloadingIds] = useState(new Set());
  const { selectedTimeZone } = useTimezone();

  const historyQuery = useQuery({
    queryKey: ['scheduledReport', 'history', reportId],
    enabled: sessionStatus === 'ok' && !!reportId,
    placeholderData: [],
    queryFn: ({ signal }) => getReportHistory(signal, reportId)
  });

  const historyData = historyQuery.data || [];

  const formatDate = (dateStr) => {
    if (!dateStr) return '--';
    return selectedTimeZone
      ? moment.tz(dateStr, selectedTimeZone).format(DATE_TIME_FORMAT)
      : moment(dateStr).format(DATE_TIME_FORMAT);
  };

  const getStatusChip = (status) => {
    const statusLower = (status || '').toLowerCase();
    let color = 'default';
    if (statusLower === 'delivered' || statusLower === 'generated') color = 'success';
    else if (statusLower === 'failed') color = 'error';
    else if (statusLower === 'generating' || statusLower === 'inprogress') color = 'warning';
    return <Chip label={status || '--'} size="small" color={color} variant="outlined" />;
  };

  const handleDownload = async (execution) => {
    setDownloadingIds((prev) => new Set(prev).add(execution.id));
    try {
      const controller = new AbortController();
      await downloadExecutionReport(controller.signal, execution.id, execution.generatedReportId);
    } finally {
      setDownloadingIds((prev) => {
        const next = new Set(prev);
        next.delete(execution.id);
        return next;
      });
    }
  };

  return (
    <TableRow>
      <TableCell colSpan={numColumns} sx={{ p: 0 }}>
        <Paper sx={{ m: 2, p: 2 }} elevation={0} variant="outlined">
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            Execution History
          </Typography>
          {historyQuery.isFetching ? (
            <Stack alignItems="center" sx={{ py: 3 }}>
              <CircularProgress size={28} />
            </Stack>
          ) : historyData.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No execution history available.
            </Typography>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Box
                component="table"
                sx={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  '& th, & td': {
                    px: 1.5,
                    py: 1,
                    textAlign: 'left',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    fontSize: '13px'
                  },
                  '& th': { fontWeight: 600 }
                }}>
                <thead>
                  <tr>
                    <th>Execution Date</th>
                    <th>Status</th>
                    <th>Period Start</th>
                    <th>Period End</th>
                    <th>Delivered To</th>
                    <th style={{ width: '48px' }} />
                  </tr>
                </thead>
                <tbody>
                  {historyData.map((execution) => (
                    <tr key={execution.id}>
                      <td>{formatDate(execution.executionDate)}</td>
                      <td>{getStatusChip(execution.status)}</td>
                      <td>{formatDate(execution.periodStart)}</td>
                      <td>{formatDate(execution.periodEnd)}</td>
                      <td>
                        {Array.isArray(execution.deliveredToEmails)
                          ? execution.deliveredToEmails.join(', ')
                          : '--'}
                      </td>
                      <td>
                        {execution.generatedReportId && (
                          <Tooltip title="Download report">
                            <IconButton
                              size="small"
                              disabled={downloadingIds.has(execution.id)}
                              onClick={() => handleDownload(execution)}>
                              {downloadingIds.has(execution.id) ? (
                                <CircularProgress size={18} />
                              ) : (
                                <FileDownloadOutlinedIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Box>
            </Box>
          )}
        </Paper>
      </TableCell>
    </TableRow>
  );
}

export default HistoryDetailsView;
